import React, { useEffect, useContext, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useGetGroupsChanges } from "../../hooks/useGetGroupChanges";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import Layout from "@common/layout/Layout";
import GroupsHistoryTable from "../../components/groups-history-table";
import ChangesHistoryResults from "@modules/common/changes-history-results";
import * as mouseflow from "@utils/mouseflow";
import buildReport from "../../utils/reports";
import { Context } from "../../context/GroupsManagerContext";
import { Types as FilterTypes } from "../../context/filters.reducer";
import {
  GroupsTableFiltersState,
  setTableFiltersOrderBy,
  useGroupsTableFilters,
} from "../../context/useGroupsTableFilters";
import { FiltersReducerType } from "../../context/types";
import { PathParams } from "@edhtypes/general";
import { ContextEDH } from "@context/ContextEDH";
import { GroupsManagerGroupChangesOrderByFieldsEnum, OrderByDirection } from "@models/ISchema";

/**
 * ChangeHistory component
 *
 * Displays a paginated, sortable table of group changes history with filtering capabilities.
 * Allows users to view all changes or filter to see only their own changes.
 * Provides functionality to download reports and refresh data.
 */
const ChangeHistory: React.FC = () => {
  const { origin, scenario, workspace }: PathParams = useParams();
  const [internalPageSize, setInternalPageSize] = useState(5);
  const [columns, setColumns] = useState([]);
  const { state, dispatch } = useContext(Context);
  const tableFilters = useGroupsTableFilters();
  const { state: outerState } = useContext(ContextEDH);
  const [groupChanges, loading, pageInfo, getGroupChanges] = useGetGroupsChanges(scenario, origin);
  const { t } = useTranslation();
  const { homeUrl, adaptedHomeUrl } = useNavigationUrl();

  /**
   * Fetches group changes data based on provided parameters
   * Updates internal page size state if it has changed
   */
  const requestGroupChanges = ({
    owned,
    page,
    orderBy,
    searchBy,
    pageSize = internalPageSize,
  }: {
    owned: boolean;
    page: number;
    orderBy: GroupsTableFiltersState["orderBy"];
    searchBy: GroupsTableFiltersState["searchBy"];
    pageSize?: number;
  }) => {
    getGroupChanges({ page, owned, groupId: "", orderBy, searchBy, pageSize });
    if (pageSize !== internalPageSize) {
      setInternalPageSize(pageSize);
    }
  };

  useEffect(() => {
    requestGroupChanges({
      owned: state?.filters?.owned,
      page: 1,
      orderBy: tableFilters.orderBy,
      searchBy: tableFilters.searchBy,
      pageSize: internalPageSize,
    });
  }, [internalPageSize]);

  // Initialize component on mount
  useEffect(() => {
    // Initial data fetch
    requestGroupChanges({
      owned: state?.filters?.owned,
      page: 1,
      orderBy: tableFilters.orderBy,
      searchBy: tableFilters.searchBy,
    });

    // Set default filter to show all changes (not just user's own)
    dispatch({
      type: FilterTypes.SetAdvancedFilter,
      payload: { filter: "owned", value: false },
    });

    // Set default sort order to date descending (newest first)
    setTableFiltersOrderBy({
      header: "Fecha",
      direction: OrderByDirection.Desc,
      field: GroupsManagerGroupChangesOrderByFieldsEnum.Date,
    });
  }, []);

  const lastUpdateDateTime = dayjs().format("DD/MM/YYYY HH:mm");

  /**
   * Generates and opens a report in a new tab
   * Tracks the action with mouseflow for analytics
   */
  const handleDownloadReport = () => {
    const reportLink = buildReport("changeHistory", {
      context: { origin, scenario },
      filters: { owned: state?.filters?.owned } as FiltersReducerType,
    });
    mouseflow.actionTag(
      "action_groups_manager_change_history_report",
      outerState?.base?.isMouseflowEnabled,
    );
    window.open(reportLink, "_blank");
  };

  /**
   * Refreshes the data with current filter settings
   * Resets to first page when refreshing
   */
  const handleUpdateView = () => {
    requestGroupChanges({
      owned: state?.filters?.owned,
      page: 1,
      orderBy: tableFilters.orderBy,
      searchBy: tableFilters.searchBy,
    });
  };

  /**
   * Toggles between showing all changes or only the current user's changes
   * Updates filter state and refetches data with new filter
   */
  const handleShowSelfChanges = () => {
    const value = !state?.filters?.owned;
    dispatch({
      type: FilterTypes.SetAdvancedFilter,
      payload: { filter: "owned", value },
    });
    requestGroupChanges({
      owned: value,
      page: 1,
      orderBy: tableFilters.orderBy,
      searchBy: tableFilters.searchBy,
    });
  };

  const dynamicTitle =
    pageInfo?.total !== 1
      ? t("group-changes-history.header.title-plural")
      : t("group-changes-history.header.title");

  return (
    <Layout contextSearch>
      <ChangesHistoryResults
        columns={columns}
        rows={internalPageSize}
        subtitle={`${pageInfo?.total} ${dynamicTitle}`}
        title={t("group-changes-history.title")}
        handleDownloadReport={handleDownloadReport}
        handleUpdateView={handleUpdateView}
        lastUpdateDateTime={lastUpdateDateTime}
        labelLastUpdate={t("group-changes-history.header.last-update")}
        labelSwitch={t("group-changes-history.switch.title")}
        showSelfChanges={state?.filters?.owned}
        setShowSelfChanges={handleShowSelfChanges}
        buttonDownloadReportLabel={t("group-changes-history.header.btn-download-report")}
        buttonUpdateViewLabel={t("group-changes-history.header.btn-update-view")}
        breadcrumbItems={[
          {
            title: t("group-changes-history.breadcrumb.prev"),
            path: adaptedHomeUrl(),
          },
          {
            title: t("group-changes-history.breadcrumb.current"),
          },
        ]}
        loading={loading}
      >
        <GroupsHistoryTable
          setColumns={setColumns}
          onChangeSizePerPage={setInternalPageSize}
          pageSize={internalPageSize}
          pageInfo={pageInfo}
          data={groupChanges}
          requestGroupCapacityChangeDetails={(
            page: number,
            orderBy: GroupsTableFiltersState["orderBy"],
            searchBy: GroupsTableFiltersState["searchBy"],
          ) => requestGroupChanges({ owned: state?.filters?.owned, page, orderBy, searchBy })}
          requestGroupChanges={(page, orderBy) => {
            // We're ignoring the searchBy from the table component and using our own
            requestGroupChanges({
              owned: state?.filters?.owned,
              page,
              orderBy,
              searchBy: tableFilters.searchBy,
            });
          }}
          contextUrl={homeUrl(workspace, scenario, origin)}
        />
      </ChangesHistoryResults>
    </Layout>
  );
};

export default ChangeHistory;
